// Canonical Run state machine. P1: authoritative on the .app (renderer).
// Mirror server-side in P3 (do not fork).
//
// Pure data + pure functions, framework-free. The ONLY import is the domain
// error type. This module is the single source of truth for which Run state
// transitions are legal; both the Run aggregate (P1) and the server runner
// (P3) MUST defer to this table rather than re-encoding the edges.

import { HermesDomainError } from './errors.js';

export const STATES = Object.freeze({
  QUEUED: 'queued',
  RUNNING: 'running',
  FANNING_OUT: 'fanning_out',
  MERGING: 'merging',
  DONE: 'done',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
});

// The three absorbing states — once here, a Run never transitions again.
export const TERMINAL = Object.freeze(['done', 'failed', 'cancelled']);

// Allowed next-states for each state. An empty array means terminal.
// Note: fanning_out cannot go directly to done — a fanned-out run can ONLY
// reach done via merging. That is the structural half of the fan-out merge
// invariant (the other half — children must be terminal — lives in Run).
export const TRANSITIONS = Object.freeze({
  queued: ['running', 'cancelled'],
  running: ['fanning_out', 'done', 'failed', 'cancelled'],
  fanning_out: ['merging', 'failed', 'cancelled'],
  merging: ['done', 'failed', 'cancelled'],
  done: [],
  failed: [],
  cancelled: [],
});

export function isTerminal(state) {
  return TERMINAL.includes(state);
}

export function canTransition(from, to) {
  const allowed = TRANSITIONS[from];
  if (!allowed) return false;
  return allowed.includes(to);
}

export function assertTransition(from, to) {
  if (!canTransition(from, to)) {
    throw new HermesDomainError(
      'illegal_transition',
      `Illegal Run transition: ${from} -> ${to}`,
    );
  }
}
